// pages/Comparison.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import PathwayMultiSelect from "../components/comparison/PathwayMultiSelect";

import FoodIntakeChart from "../components/comparison/FoodIntakeChart";
import FoodIntakeByGroupChart from "../components/comparison/FoodIntakeByGroupChart";
import LandUseChart from "../components/comparison/LandUseChart";
import GHGChart from "../components/comparison/GHGChart";
import BiodiversityNaturalChart from "../components/comparison/BiodiversityNaturalChart";
import BiodiversityProtectedChart from "../components/comparison/BiodiversityProtectedChart";

import FancySelectNativeLook from "../components/Select"; // ✅ ajusta ruta si está en otro folder

export default function Comparison({ t = (k, o) => o?.defaultValue ?? k }) {
  // =========================================
  // Load available pathways from backend
  // =========================================
  const [allPathways, setAllPathways] = useState([]);
  const [pathwaysLoading, setPathwaysLoading] = useState(true);
  const [pathwaysError, setPathwaysError] = useState("");

  // =========================================
  // Load comparisonData from backend
  // =========================================
  const [comparisonData, setComparisonData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { authFetch } = useAuth();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  // Load available pathways
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function loadPathways() {
      try {
        setPathwaysLoading(true);
        setPathwaysError("");

        const res = await authFetchRef.current("/api/sendtask/pathways", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });

        const text = await res.text();

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} - ${text.slice(0, 120)}`);
        }

        if (
          !(res.headers.get("content-type") || "").includes("application/json")
        ) {
          throw new Error(
            `Expected JSON, got HTML/other. Body: ${text.slice(0, 120)}`,
          );
        }

        const json = JSON.parse(text);
        const pathwaysArray = json?.pathways || [];
        const pathwayNames = pathwaysArray.map((p) => p.name).filter(Boolean);
        
        if (alive) setAllPathways(pathwayNames);
      } catch (e) {
        if (alive) setPathwaysError(String(e?.message || e));
      } finally {
        if (alive) setPathwaysLoading(false);
      }
    }

    loadPathways();
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  // =========================================
  // Selected pathways (max 5)
  // =========================================
  const [selectedPathways, setSelectedPathways] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("selectedPathways") || "[]",
      );
      return Array.isArray(saved) ? saved.slice(0, 5) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("selectedPathways", JSON.stringify(selectedPathways));
  }, [selectedPathways]);

  useEffect(() => {
    setSelectedPathways((prev) =>
      prev.filter((p) => allPathways.includes(p)).slice(0, 5),
    );
  }, [allPathways]);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function load() {
      try {
        setLoading(true);
        setError("");

        const url = "/api/comparison/indicators";

        const res = await authFetchRef.current(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });

        const text = await res.text();

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} - ${text.slice(0, 120)}`);
        }

        if (
          !(res.headers.get("content-type") || "").includes("application/json")
        ) {
          throw new Error(
            `Expected JSON, got HTML/other. Body: ${text.slice(0, 120)}`,
          );
        }

        const json = JSON.parse(text);
        const data =
          json?.data && typeof json.data === "object" ? json.data : {};
        if (alive) setComparisonData(data);
      } catch (e) {
        if (alive) setError(String(e?.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  // =========================================
  // Colors for pathways (stable + persisted)
  // IMPORTANT: no setState inside render.
  // =========================================
  const colorPool = ["deepskyblue", "orange", "purple", "teal", "tomato"];

  const [colorMap, setColorMap] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("comparison_colorMap") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!allPathways.length) return;

    setColorMap((prev) => {
      const next = { ...prev };
      const used = new Set(Object.values(next));

      for (const name of allPathways) {
        if (next[name]) continue;

        const pick =
          colorPool.find((c) => !used.has(c)) ||
          colorPool[Object.keys(next).length % colorPool.length];

        next[name] = pick;
        used.add(pick);
      }

      localStorage.setItem("comparison_colorMap", JSON.stringify(next));
      return next;
    });
  }, [allPathways]);

  const getColorByName = (name) => colorMap[name] || "deepskyblue";

  // =========================================
  // Filters (persist)
  // =========================================
  const [foodProduct, setFoodProduct] = useState(
    () => localStorage.getItem("selectedProduct") || "",
  );
  const [landIndicator, setLandIndicator] = useState(
    () => localStorage.getItem("selectedLanduseIndicator") || "",
  );
  const [ghgIndicator, setGhgIndicator] = useState(
    () => localStorage.getItem("selectedGHGIndicator") || "",
  );
  const [bioIndicator, setBioIndicator] = useState(
    () => localStorage.getItem("selectedBioIndicator") || "",
  );

  useEffect(
    () => localStorage.setItem("selectedProduct", foodProduct || ""),
    [foodProduct],
  );
  useEffect(
    () => localStorage.setItem("selectedLanduseIndicator", landIndicator || ""),
    [landIndicator],
  );
  useEffect(
    () => localStorage.setItem("selectedGHGIndicator", ghgIndicator || ""),
    [ghgIndicator],
  );
  useEffect(
    () => localStorage.setItem("selectedBioIndicator", bioIndicator || ""),
    [bioIndicator],
  );

  // =========================================
  // Tabs
  // =========================================
  const [activeTab, setActiveTab] = useState("Food");
  const [activeFoodSub, setActiveFoodSub] = useState("a1"); // a1, a2
  const [activeBioSub, setActiveBioSub] = useState("e1"); // e1, e2

  // =========================================
  // Options for selects (from first pathway)
  // IMPORTANT: backend returns { charts: {...} }
  // =========================================
  const firstPathway = allPathways[0];

  const food2Options = useMemo(() => {
    const ds =
      comparisonData?.[firstPathway]?.charts?.chartFood2?.datasets || [];
    const list = Array.isArray(ds)
      ? ds.map((d) => d?.label).filter(Boolean)
      : [];
    return list;
  }, [comparisonData, firstPathway]);

  const landOptions = useMemo(() => {
    const ds =
      comparisonData?.[firstPathway]?.charts?.chartLand1?.datasets || [];
    if (!Array.isArray(ds)) return [];
    return ds.map((d) => d?.label).filter(Boolean);
  }, [comparisonData, firstPathway]);

  const ghgOptions = useMemo(() => {
    const ds =
      comparisonData?.[firstPathway]?.charts?.chartghg1?.datasets || [];
    return Array.isArray(ds) ? ds.map((d) => d?.label).filter(Boolean) : [];
  }, [comparisonData, firstPathway]);

  const bio2Options = useMemo(() => {
    const ds =
      comparisonData?.[firstPathway]?.charts?.chartbio2?.datasets || [];
    return Array.isArray(ds) ? ds.map((d) => d?.label).filter(Boolean) : [];
  }, [comparisonData, firstPathway]);

  // Convert string[] => [{value,label}] for FancySelectNativeLook
  const toFancyOptions = (arr) =>
    Array.isArray(arr) ? arr.map((x) => ({ value: x, label: x })) : [];

  const food2Fancy = useMemo(
    () => toFancyOptions(food2Options),
    [food2Options],
  );
  const landFancy = useMemo(() => toFancyOptions(landOptions), [landOptions]);
  const ghgFancy = useMemo(() => toFancyOptions(ghgOptions), [ghgOptions]);
  const bio2Fancy = useMemo(() => toFancyOptions(bio2Options), [bio2Options]);

  // set defaults when options appear
  useEffect(() => {
    if (!foodProduct && food2Options.length) setFoodProduct(food2Options[0]);
  }, [food2Options, foodProduct]);

  useEffect(() => {
    if (!landIndicator && landOptions.length) setLandIndicator(landOptions[0]);
  }, [landOptions, landIndicator]);

  useEffect(() => {
    if (!ghgIndicator && ghgOptions.length) setGhgIndicator(ghgOptions[0]);
  }, [ghgOptions, ghgIndicator]);

  useEffect(() => {
    if (!bioIndicator && bio2Options.length) setBioIndicator(bio2Options[0]);
  }, [bio2Options, bioIndicator]);

  // =========================================
  // UI helpers
  // =========================================
  const TabButton = ({ k, label }) => {
    const isActive = activeTab === k;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(k)}
        className="relative pb-2"
      >
        <span className="px-2">{label}</span>
        {isActive && (
          <span className="absolute left-0 right-0 -bottom-[3px] h-[3px] bg-[#54B046] rounded-full w-full mx-auto" />
        )}
      </button>
    );
  };

  const SubTabButton = ({ k, label, value, onChange }) => {
    const isActive = value === k;
    return (
      <button
        type="button"
        onClick={() => onChange(k)}
        className="relative pb-2"
      >
        <span className="px-2">{label}</span>
        {isActive && (
          <span className="absolute left-0 right-0 -bottom-[3px] h-[3px] bg-[#54B046] rounded-full w-full mx-auto" />
        )}
      </button>
    );
  };

  // =========================================
  // Loading / Error
  // =========================================
  if (pathwaysError || error) {
    return (
      <div className="p-6">
        <div className="rounded-xl bg-white p-6 ring-1 ring-[#E3E5E6] text-red-600">
          {pathwaysError || error}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-10 w-full rounded-xl bg-slate-50 shadow-sm">
      <main className="mx-auto w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Selector */}
        <div className="rounded-xl bg-white p-6 ring-1 ring-[#E3E5E6]">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[0.7fr_1.3fr] md:gap-6">
            {/* LEFT: Select */}
            <div className="min-w-[320px]">
              <div className="text-sm font-semibold text-[#101010] mb-2">
                {t("comparison.selector.label", {
                  defaultValue: "Select pathways to compare",
                })}
              </div>

              <PathwayMultiSelect
                pathways={allPathways}
                selected={selectedPathways}
                setSelected={setSelectedPathways}
                getColorByName={getColorByName}
                max={5}
                placeholder={t("comparison.selector.placeholder", {
                  defaultValue: "Select Pathways",
                })}
              />

              <div className="mt-2 text-[12px] text-[#7E8081]">
                {selectedPathways.length}/5 selected
              </div>
            </div>

            {/* RIGHT: Selected pills */}
            <div className="md:pt-1">
              <div className="text-sm font-semibold text-[#101010] mb-2">
                {t("comparison.selector.selected", {
                  defaultValue: "Selected pathways",
                })}
              </div>

              {selectedPathways.length === 0 ? (
                <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200 text-sm text-slate-700">
                  {t("comparison.selector.none", {
                    defaultValue: "No pathways selected yet.",
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedPathways.map((p) => (
                    <div
                      key={p}
                      className="flex items-center gap-2 rounded-full border border-[#E3E5E6] bg-white px-3 py-2 shadow-sm"
                      title={p}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: getColorByName(p) }}
                      />
                      <span className="text-[13px] text-[#101010] max-w-[260px] truncate">
                        {p}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          setSelectedPathways((prev) =>
                            prev.filter((x) => x !== p),
                          )
                        }
                        className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[#7E8081] hover:bg-[#F7F9FA] hover:text-[#101010]"
                        aria-label={`Remove ${p}`}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Optional: Clear all */}
              {selectedPathways.length > 0 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setSelectedPathways([])}
                    className="text-[13px] text-[#7E8081] hover:text-[#101010]"
                  >
                    {t("comparison.selector.clear", {
                      defaultValue: "Clear selection",
                    })}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs principales */}
        <div className="mt-6 w-full rounded-xl bg-white overflow-hidden">
          <div className="border-b border-[#E3E5E6] px-8 pt-4 flex justify-center gap-10 text-sm font-medium text-[#101010]">
            <TabButton
              k="Food"
              label={t("comparison.tabs.food", { defaultValue: "Food" })}
            />
            <TabButton
              k="Land use"
              label={t("comparison.tabs.land", { defaultValue: "Land Use" })}
            />
            <TabButton
              k="GHG"
              label={t("comparison.tabs.ghg", { defaultValue: "GHG" })}
            />
            <TabButton
              k="Biodiversity"
              label={t("comparison.tabs.biodiversity", {
                defaultValue: "Biodiversity",
              })}
            />
          </div>

          <div className="px-8 py-6">
            {selectedPathways.length === 0 ? (
              <div className="rounded-xl bg-slate-50 p-6 ring-1 ring-slate-200 text-sm text-slate-700">
                Select at least 1 pathway to view the comparison.
              </div>
            ) : (
              <>
                {/* FOOD */}
                {activeTab === "Food" && (
                  <div className="space-y-5">
                    <div className="border-b border-[#E3E5E6] pb-2 flex gap-8 text-sm font-medium text-[#101010]">
                      <SubTabButton
                        k="a1"
                        value={activeFoodSub}
                        onChange={setActiveFoodSub}
                        label={t("comparison.food.intake", {
                          defaultValue: "Daily per capita food intake",
                        })}
                      />
                      <SubTabButton
                        k="a2"
                        value={activeFoodSub}
                        onChange={setActiveFoodSub}
                        label={t("comparison.food.intakeByGroup", {
                          defaultValue: "Daily per capita food intake by group",
                        })}
                      />
                    </div>

                    {activeFoodSub === "a1" && (
                      <FoodIntakeChart
                        comparisonData={comparisonData}
                        selectedPathways={selectedPathways}
                        getColorByName={getColorByName}
                      />
                    )}

                    {activeFoodSub === "a2" && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 max-w-[380px]">
                          <FancySelectNativeLook
                            label="Product"
                            value={foodProduct}
                            onChange={setFoodProduct}
                            options={food2Fancy}
                            placeholder="Filter product..."
                            disabled={!food2Fancy.length}
                          />
                        </div>

                        <FoodIntakeByGroupChart
                          comparisonData={comparisonData}
                          selectedPathways={selectedPathways}
                          product={foodProduct}
                          getColorByName={getColorByName}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* LAND USE */}
                {activeTab === "Land use" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 max-w-[380px]">
                      <FancySelectNativeLook
                        label="Land indicator"
                        value={landIndicator}
                        onChange={setLandIndicator}
                        options={landFancy}
                        placeholder="Select land indicator..."
                        disabled={!landFancy.length}
                      />
                    </div>

                    <LandUseChart
                      comparisonData={comparisonData}
                      selectedPathways={selectedPathways}
                      indicator={landIndicator}
                      getColorByName={getColorByName}
                    />
                  </div>
                )}

                {/* GHG */}
                {activeTab === "GHG" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 max-w-[380px]">
                      <FancySelectNativeLook
                        label="GHG indicator"
                        value={ghgIndicator}
                        onChange={setGhgIndicator}
                        options={ghgFancy}
                        placeholder="Select GHG indicator..."
                        disabled={!ghgFancy.length}
                      />
                    </div>

                    <GHGChart
                      comparisonData={comparisonData}
                      selectedPathways={selectedPathways}
                      indicator={ghgIndicator}
                      getColorByName={getColorByName}
                    />
                  </div>
                )}

                {/* BIODIVERSITY */}
                {activeTab === "Biodiversity" && (
                  <div className="space-y-5">
                    <div className="border-b border-[#E3E5E6] pb-2 flex gap-8 text-sm font-medium text-[#101010]">
                      <SubTabButton
                        k="e1"
                        value={activeBioSub}
                        onChange={setActiveBioSub}
                        label={t("comparison.bio.naturalProcesses", {
                          defaultValue:
                            "Land where natural processes predominate",
                        })}
                      />
                      <SubTabButton
                        k="e2"
                        value={activeBioSub}
                        onChange={setActiveBioSub}
                        label={t("comparison.bio.protectedEvolution", {
                          defaultValue: "Evolution of Protected Areas",
                        })}
                      />
                    </div>

                    {activeBioSub === "e1" && (
                      <BiodiversityNaturalChart
                        comparisonData={comparisonData}
                        selectedPathways={selectedPathways}
                        getColorByName={getColorByName}
                      />
                    )}

                    {activeBioSub === "e2" && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 max-w-[380px]">
                          <FancySelectNativeLook
                            label="Biodiversity indicator"
                            value={bioIndicator}
                            onChange={setBioIndicator}
                            options={bio2Fancy}
                            placeholder="Select biodiversity indicator..."
                            disabled={!bio2Fancy.length}
                          />
                        </div>

                        <BiodiversityProtectedChart
                          comparisonData={comparisonData}
                          selectedPathways={selectedPathways}
                          indicator={bioIndicator}
                          getColorByName={getColorByName}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
